import { useRef, useState } from "react";
import { BarChart3, Layers3, Wallet, Workflow } from "lucide-react";
import { BottomNav } from "../../components/BottomNav";
import { navAnimatedIcons } from "../../components/navAnimatedIcons";
import { useAccentButtonHaptics } from "../../lib/useAccentButtonHaptics";
import { FlowWizard } from "./pages/FlowWizard";
import { FlowsList } from "./pages/FlowsList";
import { DriverBalance } from "./pages/Balance";
import { OwnResources } from "./pages/OwnResources";
import { DriverStats } from "./pages/Stats";

type View =
  | { kind: "list" }
  | { kind: "create" }
  | { kind: "edit"; flowId: number }
  | { kind: "flow_stats"; flowId: number; flowName?: string }
  | { kind: "balance" }
  | { kind: "resources" }
  | { kind: "stats" };

export default function DriverMode({ me: _me }: { me: unknown }) {
  const shellRef = useRef<HTMLDivElement>(null);
  const [view, setView] = useState<View>(() => initialView());
  useAccentButtonHaptics(shellRef);
  const activeTab =
    view.kind === "balance"
      ? "balance"
      : view.kind === "stats"
        ? "stats"
      : view.kind === "resources"
        ? "resources"
        : view.kind === "list"
          ? "list"
          : "list";

  return (
    <div className="tp-shell" ref={shellRef}>
      {view.kind === "balance" && <DriverBalance onBack={() => setView({ kind: "list" })} />}
      {view.kind === "resources" && <OwnResources />}
      {view.kind === "stats" && <DriverStats key="account-stats" />}
      {view.kind === "flow_stats" && (
        <DriverStats
          key={`flow-stats-${view.flowId}`}
          flowId={view.flowId}
          flowName={view.flowName}
          onBack={() => setView({ kind: "list" })}
        />
      )}

      {view.kind === "list" && (
        <FlowsList
          onCreate={() => setView({ kind: "create" })}
          onEdit={(id) => setView({ kind: "edit", flowId: id })}
          onStats={(id, name) => setView({ kind: "flow_stats", flowId: id, flowName: name })}
        />
      )}

      {view.kind === "create" && (
        <FlowWizard
          mode="create"
          onDone={() => setView({ kind: "list" })}
          onCancel={() => setView({ kind: "list" })}
        />
      )}

      {view.kind === "edit" && (
        <FlowWizard
          mode="edit"
          flowId={view.flowId}
          onDone={() => setView({ kind: "list" })}
          onCancel={() => setView({ kind: "list" })}
        />
      )}

      <BottomNav
        items={[
          {
            key: "list",
            label: "меню",
            icon: Workflow,
            animatedIcon: navAnimatedIcons.home,
            active: activeTab === "list",
            onClick: () => setView({ kind: "list" }),
          },
          {
            key: "stats",
            label: "статистика",
            icon: BarChart3,
            animatedIcon: navAnimatedIcons.stats,
            active: activeTab === "stats",
            onClick: () => setView({ kind: "stats" }),
          },
          {
            key: "resources",
            label: "ресурсы",
            icon: Layers3,
            animatedIcon: navAnimatedIcons.resources,
            active: activeTab === "resources",
            onClick: () => setView({ kind: "resources" }),
          },
          {
            key: "balance",
            label: "баланс",
            icon: Wallet,
            active: activeTab === "balance",
            onClick: () => setView({ kind: "balance" }),
            avatar: true,
          },
        ]}
      />
    </div>
  );
}

function initialView(): View {
  const tab = new URLSearchParams(window.location.search).get("tab");
  if (tab === "new-flow") return { kind: "create" };
  if (tab === "balance") return { kind: "balance" };
  if (tab === "resources") return { kind: "resources" };
  if (tab === "stats") return { kind: "stats" };
  return { kind: "list" };
}
